import { Box, WarningIcon } from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useIsUsageEnabled } from "src/components/lineage/use-is-usage-enabled";
import { useIsPredictiveTraitsEnabled } from "src/components/traits/use-predictive-traits-enabled";
import { SchemaModelType } from "src/types/schema";
import { SchemaObjectType } from "src/pages/schema/types";

const TYPES_WITH_CONFIGURATION = [
  SchemaModelType.Parent,
  SchemaModelType.Event,
  SchemaModelType.Interaction,
  SchemaModelType.Related,
  SchemaModelType.Asset,
  SchemaModelType.Catalog,
  SchemaModelType.AdStats,
  SchemaModelType.RealtimeEvent,
];

export function shouldShowConfigurationSection(type: SchemaModelType) {
  return TYPES_WITH_CONFIGURATION.includes(type);
}

const TYPES_WITH_SAMPLING = [
  SchemaModelType.Parent,
  SchemaModelType.Event,
  SchemaModelType.Related,
];

export function useTabRoutes(opts: {
  schemaModelType: SchemaModelType;
  sourceSupportsSampling: boolean;
  sourceSupportsPredictors: boolean;
  isProbabilisticGoldenRecordModel: boolean;
  columnCount?: number;
  hasRelationshipWarnings?: boolean;
  usageCount?: number;
}) {
  const { schemaModelType, sourceSupportsSampling, sourceSupportsPredictors } =
    opts;
  const { appModelSamplingEnabled } = useFlags();
  const isUsageEnabled = useIsUsageEnabled();
  const predictiveTraitsEnabled = useIsPredictiveTraitsEnabled();

  return [
    {
      title: "Columns",
      path: "columns",
      count: opts.columnCount,
    },

    {
      title: "Relationships",
      path: "relationships",
      statusIndicator: opts.hasRelationshipWarnings ? (
        <Box as={WarningIcon} color="warning.base" fontSize="18px" />
      ) : undefined,
    },

    {
      title: "Match Booster",
      path: "match-booster",
      isHidden: schemaModelType !== SchemaModelType.Parent,
    },
    {
      title: "Sampling",
      path: "sampling",
      isHidden:
        !appModelSamplingEnabled ||
        !TYPES_WITH_SAMPLING.includes(schemaModelType) ||
        !sourceSupportsSampling,
    },
    {
      title: "Confidence levels",
      path: "confidence-levels",
      isHidden: !opts.isProbabilisticGoldenRecordModel,
    },
    {
      title: "Predictive Inputs",
      path: "predictive",
      isHidden:
        schemaModelType !== SchemaModelType.Parent ||
        !predictiveTraitsEnabled ||
        !sourceSupportsPredictors,
    },
    {
      title: "Recommendations",
      path: "recommendations",
      isHidden:
        !predictiveTraitsEnabled || schemaModelType !== SchemaModelType.Catalog,
    },
    {
      title: "Usage",
      path: "usage",
      count: opts.usageCount,
      isHidden: !isUsageEnabled,
    },
    {
      title: "Activity",
      path: "activity",
      isHidden: schemaModelType !== SchemaModelType.Parent,
    },
    {
      title: "Configuration",
      path: "query",
    },
  ];
}

export function showMissingModelWarning({
  object,
  campaignsEnabled,
}: {
  object: SchemaObjectType;
  campaignsEnabled: boolean;
}) {
  // Never show if not an interaction or campaigns are not enabled
  if (object.type !== SchemaModelType.Interaction || !campaignsEnabled) {
    return false;
  }

  const hasAssetRelationship = object.relationships.some(
    (relationship) => relationship.to_model.type === SchemaModelType.Asset,
  );

  // Only show the warning if there is no asset relationship
  return !hasAssetRelationship;
}

export const createSchemaObjectLink = (
  sourceId: string,
  modelId: string,
  path: string,
) => `/schema-v2/view/${path}?source=${sourceId}&id=${modelId}`;
